/**
 * Weapon-specific projectile models and exhaust effects.
 * Each of the 6 weapons has a unique projectile shape and exhaust configuration.
 * Ported from design_sheets/projectiles.html.
 *
 * Models are built at design-sheet scale; apply PROJECTILE_SCALE when adding to scene.
 */

import * as THREE from 'three';
import type { WeaponKey } from '../types';
import { WEAPONS } from '../constants';
import { buildWeaponModel } from './weaponModels';
import { markShared } from './meshBake';

// =============================================================================
// SCALE — design sheet is 3× game size
// =============================================================================

export const PROJECTILE_SCALE = 1 / 3;

// =============================================================================
// MATERIAL HELPERS
// =============================================================================

function tintColor(hex: string, amount: number): THREE.Color {
  const c = new THREE.Color(hex);
  c.r = c.r + (1 - c.r) * amount;
  c.g = c.g + (1 - c.g) * amount;
  c.b = c.b + (1 - c.b) * amount;
  return c;
}

function mat(color: string | THREE.Color): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: color instanceof THREE.Color ? color : new THREE.Color(color) });
}

// =============================================================================
// MODEL — the weapon bodies live in weaponModels.ts (ordnance kit)
// =============================================================================

export function buildProjectileModel(weaponKey: WeaponKey): THREE.Group {
  return buildWeaponModel(weaponKey);
}

// =============================================================================
// EXHAUST CONFIGURATION — per-weapon flame parameters
// =============================================================================

export interface ExhaustConfig {
  coneLen: number;
  coneRad: number;
  innerLen: number;
  innerRad: number;
  particles: number;
  particleSize: number;
  spread: number;
  glowIntensity: number;
  glowRange: number;
  /** Z offset from projectile center to nozzle (where exhaust attaches) */
  nozzleOffset: number;
}

export const EXHAUST_CONFIGS: Record<WeaponKey, ExhaustConfig> = {
  // standard: medium flame
  standard: { coneLen: 0.5, coneRad: 0.12, innerLen: 0.35, innerRad: 0.06,
    particles: 15, particleSize: 0.08, spread: 0.15, glowIntensity: 1.5, glowRange: 6, nozzleOffset: -0.35 },
  // mortar: a short puff — the field lob (S4); same shape as standard
  mortar: { coneLen: 0.4, coneRad: 0.12, innerLen: 0.3, innerRad: 0.06,
    particles: 12, particleSize: 0.08, spread: 0.15, glowIntensity: 1.2, glowRange: 5, nozzleOffset: -0.35 },
  // heavy: wide, hot flame
  heavy: { coneLen: 0.7, coneRad: 0.2, innerLen: 0.5, innerRad: 0.1,
    particles: 25, particleSize: 0.1, spread: 0.25, glowIntensity: 2.5, glowRange: 10, nozzleOffset: -0.34 },
  // cluster: compact exhaust
  cluster: { coneLen: 0.3, coneRad: 0.08, innerLen: 0.2, innerRad: 0.04,
    particles: 12, particleSize: 0.06, spread: 0.1, glowIntensity: 1.2, glowRange: 5, nozzleOffset: -0.22 },
  // nuke: large, dramatic
  nuke: { coneLen: 0.9, coneRad: 0.22, innerLen: 0.65, innerRad: 0.12,
    particles: 30, particleSize: 0.12, spread: 0.2, glowIntensity: 3.0, glowRange: 12, nozzleOffset: -0.49 },
  // probe: subtle thruster
  probe: { coneLen: 0.25, coneRad: 0.06, innerLen: 0.15, innerRad: 0.03,
    particles: 8, particleSize: 0.04, spread: 0.08, glowIntensity: 0.8, glowRange: 4, nozzleOffset: -0.28 },
  // punch: tight focused streak
  punch: { coneLen: 0.6, coneRad: 0.06, innerLen: 0.5, innerRad: 0.03,
    particles: 18, particleSize: 0.05, spread: 0.06, glowIntensity: 2.0, glowRange: 8, nozzleOffset: -0.45 },
  // antigrav: ethereal purple exhaust
  antigrav: { coneLen: 0.5, coneRad: 0.12, innerLen: 0.35, innerRad: 0.06,
    particles: 15, particleSize: 0.08, spread: 0.15, glowIntensity: 1.5, glowRange: 6, nozzleOffset: -0.30 },
  // gravBomb: heavy, deep purple
  gravBomb: { coneLen: 0.6, coneRad: 0.15, innerLen: 0.4, innerRad: 0.08,
    particles: 20, particleSize: 0.09, spread: 0.18, glowIntensity: 2.0, glowRange: 8, nozzleOffset: -0.30 },
  // tether: compact mine thruster
  tether: { coneLen: 0.3, coneRad: 0.08, innerLen: 0.2, innerRad: 0.04,
    particles: 10, particleSize: 0.06, spread: 0.1, glowIntensity: 1.0, glowRange: 4, nozzleOffset: -0.20 },
  // terraformer: organic green trail
  terraformer: { coneLen: 0.5, coneRad: 0.1, innerLen: 0.35, innerRad: 0.05,
    particles: 15, particleSize: 0.07, spread: 0.12, glowIntensity: 1.3, glowRange: 5, nozzleOffset: -0.30 },
  // emp: electric blue pulse
  emp: { coneLen: 0.4, coneRad: 0.1, innerLen: 0.3, innerRad: 0.05,
    particles: 15, particleSize: 0.07, spread: 0.12, glowIntensity: 1.5, glowRange: 6, nozzleOffset: -0.28 },
  // drill: heavy, focused thrust
  drill: { coneLen: 0.6, coneRad: 0.14, innerLen: 0.45, innerRad: 0.07,
    particles: 20, particleSize: 0.08, spread: 0.15, glowIntensity: 2.0, glowRange: 8, nozzleOffset: -0.28 },
  // fire: hot, wide flame
  fire: { coneLen: 0.6, coneRad: 0.16, innerLen: 0.45, innerRad: 0.08,
    particles: 22, particleSize: 0.1, spread: 0.2, glowIntensity: 2.5, glowRange: 10, nozzleOffset: -0.30 },
};

// =============================================================================
// EXHAUST BUILDER
// =============================================================================

export interface ExhaustParticleData {
  x: number;
  y: number;
  z: number;
  life: number;
  phase: number;
  freq: number;
  baseSize: number;
}

export interface ProjectileExhaust {
  group: THREE.Group;
  outerCone: THREE.Mesh;
  innerCone: THREE.Mesh;
  glow: THREE.Mesh;           // additive glow blob (a PointLight here forced a full shader recompile every launch/impact)
  particles: THREE.Points;
  velocities: ExhaustParticleData[];
  cfg: ExhaustConfig;
  visible: boolean;
}

export function buildExhaust(weaponKey: WeaponKey): ProjectileExhaust {
  const weapon = WEAPONS[weaponKey];
  const cfg = EXHAUST_CONFIGS[weaponKey];
  const g = new THREE.Group();
  // Everything burns from the nozzle, `nozzleOffset` behind the projectile's centre (the group sits on the centre,
  // +Z along the flight); the flame used to start at the centre, half inside the body (BUILDLIST 35)
  const nozzle = new THREE.Group();
  nozzle.position.z = cfg.nozzleOffset;
  g.add(nozzle);

  const wpnColor = new THREE.Color(weapon.color);
  const coreColor = tintColor(weapon.color, 0.7);

  // Outer flame cone
  const outerMat = new THREE.MeshBasicMaterial({
    color: wpnColor, transparent: true, opacity: 0.6, side: THREE.DoubleSide
  });
  const outerCone = new THREE.Mesh(
    new THREE.ConeGeometry(cfg.coneRad, cfg.coneLen, 8),
    outerMat
  );
  outerCone.rotation.x = Math.PI / 2; // point backward (-Z)
  outerCone.position.z = -cfg.coneLen * 0.5;
  nozzle.add(outerCone);

  // Inner flame cone (bright core)
  const innerMat = new THREE.MeshBasicMaterial({
    color: coreColor, transparent: true, opacity: 0.85
  });
  const innerCone = new THREE.Mesh(
    new THREE.ConeGeometry(cfg.innerRad, cfg.innerLen, 6),
    innerMat
  );
  innerCone.rotation.x = Math.PI / 2;
  innerCone.position.z = -cfg.innerLen * 0.5;
  nozzle.add(innerCone);

  // Glow blob — additive sphere instead of a PointLight. Adding/removing a light
  // changes the scene's light count and makes Three.js recompile every lit
  // material (the launch/impact stall). glowIntensity/glowRange scale the blob.
  const glowMat = new THREE.MeshBasicMaterial({
    color: wpnColor,
    transparent: true,
    opacity: Math.min(0.5, 0.18 + cfg.glowIntensity * 0.08),
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const glow = new THREE.Mesh(new THREE.SphereGeometry(cfg.coneRad * (1.2 + cfg.glowRange * 0.05), 10, 8), glowMat);
  nozzle.add(glow);   // on the nozzle's mouth

  // Exhaust particles
  const particleGeo = new THREE.BufferGeometry();
  const positions = new Float32Array(cfg.particles * 3);
  const velocities: ExhaustParticleData[] = [];
  for (let i = 0; i < cfg.particles; i++) {
    positions[i * 3] = (Math.random() - 0.5) * cfg.spread;
    positions[i * 3 + 1] = (Math.random() - 0.5) * cfg.spread;
    positions[i * 3 + 2] = -cfg.coneLen * 0.5 - Math.random() * cfg.coneLen;
    velocities.push({
      x: (Math.random() - 0.5) * cfg.spread * 0.5,
      y: (Math.random() - 0.5) * cfg.spread * 0.5,
      z: -(0.5 + Math.random() * 0.5),
      life: Math.random(),
      phase: Math.random() * Math.PI * 2,
      freq: 8 + Math.random() * 12,
      baseSize: cfg.particleSize * (0.7 + Math.random() * 0.6),
    });
  }
  particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const particleMat = new THREE.PointsMaterial({
    color: tintColor(weapon.color, 0.3),
    size: cfg.particleSize,
    transparent: true,
    opacity: 0.7,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const particles = new THREE.Points(particleGeo, particleMat);
  nozzle.add(particles);

  return { group: g, outerCone, innerCone, glow, particles, velocities, cfg, visible: false };
}

// =============================================================================
// EXHAUST ANIMATION
// =============================================================================

export function animateExhaust(exhaust: ProjectileExhaust, dt: number, time: number): void {
  const cfg = exhaust.cfg;

  // Flame cones — gentle asymmetric pulse
  const pulse = 0.92 + Math.sin(time * 9.5) * 0.06 + Math.sin(time * 17.3) * 0.02;
  exhaust.outerCone.scale.set(pulse, 1.0 + (pulse - 0.92) * 0.3, pulse);
  (exhaust.outerCone.material as THREE.MeshBasicMaterial).opacity = 0.55 + Math.sin(time * 11) * 0.08;

  const pulse2 = 0.94 + Math.sin(time * 13.7 + 1.8) * 0.05;
  exhaust.innerCone.scale.set(pulse2, 1.0 + (pulse2 - 0.94) * 0.2, pulse2);

  // Particle cycling
  const posAttr = exhaust.particles.geometry.attributes.position;
  for (let i = 0; i < exhaust.velocities.length; i++) {
    const v = exhaust.velocities[i];
    v.life += dt * (1.5 + Math.random() * 0.5);
    if (v.life > 1) {
      // Reset to nozzle
      posAttr.array[i * 3] = (Math.random() - 0.5) * cfg.spread * 0.3;
      posAttr.array[i * 3 + 1] = (Math.random() - 0.5) * cfg.spread * 0.3;
      posAttr.array[i * 3 + 2] = -0.05;
      v.life = 0;
      v.x = (Math.random() - 0.5) * cfg.spread * 0.5;
      v.y = (Math.random() - 0.5) * cfg.spread * 0.5;
      v.z = -(0.5 + Math.random() * 0.5);
      v.phase = Math.random() * Math.PI * 2;
      v.freq = 8 + Math.random() * 12;
    } else {
      // Drift + subtle twinkle jitter
      const twinkle = Math.sin(time * v.freq + v.phase);
      const jitter = twinkle * 0.015 * cfg.spread;
      posAttr.array[i * 3] += v.x * dt + jitter;
      posAttr.array[i * 3 + 1] += v.y * dt + jitter * 0.7;
      posAttr.array[i * 3 + 2] += v.z * dt * cfg.coneLen;
    }
  }
  posAttr.needsUpdate = true;
  // Overall particle opacity — subtle breath
  (exhaust.particles.material as THREE.PointsMaterial).opacity = 0.6 + Math.sin(time * 7) * 0.08;
}

// =============================================================================
// HEALTHPACK MODELS
// =============================================================================

const mMetal = markShared(mat('#5a5a6a'));
const mMetalDk = markShared(mat('#3a3a4a'));

function matEmissive(color: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({
    color: new THREE.Color(color),
    emissive: new THREE.Color(color),
    emissiveIntensity: 0.35,
  });
}

function matGlow(color: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({
    color: new THREE.Color(color),
    emissive: new THREE.Color(color),
    emissiveIntensity: 0.7,
  });
}

export function buildHealthSmall(): THREE.Group {
  const g = new THREE.Group();

  // Case body
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.05, 0.07), mat('#2a3a2a'));
  body.position.y = 0.035;
  g.add(body);

  // Lid
  const lid = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.008, 0.07), mat('#3a4a3a'));
  lid.position.y = 0.063;
  g.add(lid);

  // Latch
  const latch = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.012, 0.01), mMetalDk);
  latch.position.set(0, 0.04, 0.04);
  g.add(latch);

  // Red cross on lid
  const crossH = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.003, 0.012), matGlow('#cc3030'));
  crossH.position.y = 0.069;
  g.add(crossH);
  const crossV = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.003, 0.04), matGlow('#cc3030'));
  crossV.position.y = 0.069;
  g.add(crossV);

  // Injector tube
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.06, 6), matEmissive('#30cc60'));
  tube.position.set(0.03, 0.035, 0);
  tube.rotation.z = Math.PI / 2;
  g.add(tube);

  return g;
}

export function buildHealthLarge(): THREE.Group {
  const g = new THREE.Group();

  // Armored crate body
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.1, 0.12), mat('#2a3a2a'));
  body.position.y = 0.06;
  g.add(body);

  // Armored lid
  const lid = new THREE.Mesh(new THREE.BoxGeometry(0.19, 0.012, 0.13), mat('#3a4a3a'));
  lid.position.y = 0.115;
  g.add(lid);

  // Corner reinforcements
  for (const c of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
    const corner = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.11, 0.02), mMetalDk);
    corner.position.set(c[0] * 0.09, 0.06, c[1] * 0.06);
    g.add(corner);
  }

  // Red cross
  const crossH = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.004, 0.02), matGlow('#cc3030'));
  crossH.position.y = 0.123;
  g.add(crossH);
  const crossV = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.004, 0.07), matGlow('#cc3030'));
  crossV.position.y = 0.123;
  g.add(crossV);

  // Bio-gel canister
  const canister = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.07, 8), matGlow('#30cc60'));
  canister.position.set(0, 0.06, 0.065);
  g.add(canister);

  // Side window
  const window = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.04, 0.005),
    new THREE.MeshLambertMaterial({ color: new THREE.Color('#1a3a2a'), transparent: true, opacity: 0.5 })
  );
  window.position.set(0, 0.06, 0.063);
  g.add(window);

  // Diagnostic panel
  const panel = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.025, 0.005), matEmissive('#205030'));
  panel.position.set(0, 0.08, -0.063);
  g.add(panel);

  // Status dots
  for (let i = 0; i < 3; i++) {
    const dot = new THREE.Mesh(new THREE.SphereGeometry(0.004, 4, 4), matGlow(i < 2 ? '#30cc60' : '#cc8830'));
    dot.position.set(-0.015 + i * 0.015, 0.065, -0.064);
    g.add(dot);
  }

  // Carry handle
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.008, 0.015), mMetal);
  handle.position.set(0, 0.13, 0);
  g.add(handle);
  for (let s = -1; s <= 1; s += 2) {
    const bracket = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.02, 0.015), mMetal);
    bracket.position.set(s * 0.04, 0.125, 0);
    g.add(bracket);
  }

  // Latches
  for (const x of [-0.05, 0.05]) {
    const latch = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.015, 0.012), mMetalDk);
    latch.position.set(x, 0.04, -0.066);
    g.add(latch);
  }

  return g;
}
